import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PresentacionProductoComercializador } from 'src/app/productos-comercializadores/registro-productos-comercializadores/interfaces/producto-comercializador.interface';
import { IconoFiltroComponent } from 'src/app/common/components/icono-filtro/icono-filtro.component';
import { ProductosComercializadoresService } from 'src/app/productos-comercializadores/registro-productos-comercializadores/services/productos-comercializadores.service';

/**
 * Componente para mostrar la tabla de productos de comercializadores
 *
 * Este componente se encarga de renderizar la tabla con los productos
 * y gestionar la interacción del usuario con la paginación y cambios de estado.
 *
 * @example
 * <app-tabla-productos
 *   [presentaciones]="presentaciones"
 *   [paginaActual]="paginaActual"
 *   [totalPaginas]="totalPaginas"
 *   (cambiarEstado)="onCambiarEstado($event)"
 *   (paginaAnterior)="onPaginaAnterior()"
 *   (paginaSiguiente)="onPaginaSiguiente()">
 * </app-tabla-productos>
 *
 * @since 1.0.0
 */
@Component({
    selector: 'app-tabla-productos',
    standalone: true,
    imports: [CommonModule, FormsModule, IconoFiltroComponent],
    templateUrl: './tabla-productos.component.html',
    styleUrls: ['./tabla-productos.component.css'],
})
export class TablaProductosComponent implements OnInit {
    constructor(
        private readonly presentacionService: ProductosComercializadoresService
    ) {}

    ngOnInit(): void {
        this.cargarCategorias();
    }
    /**
     * Lista de presentaciones de productos a mostrar en la tabla
     */
    @Input() presentaciones: PresentacionProductoComercializador[] = [];

    /**
     * Número de la página actual
     */
    @Input() paginaActual = 1;

    /**
     * Total de páginas disponibles
     */
    @Input() totalPaginas = 0;

    categoriasProductos: any[] = [];

    estados = [
        { id: 'activo', nombre: 'Activo' },
        { id: 'inactivo', nombre: 'Inactivo' },
    ];
    /**
     * Evento emitido cuando se cambia el estado de un producto
     */
    @Output() cambiarEstado =
        new EventEmitter<PresentacionProductoComercializador>();

    /**
     * Evento emitido cuando se solicita navegar a la página anterior
     */
    @Output() paginaAnterior = new EventEmitter<void>();

    /**
     * Evento emitido cuando se solicita navegar a la página siguiente
     */
    @Output() paginaSiguiente = new EventEmitter<void>();

    @Output() filtrar = new EventEmitter<{ columna: string; valor: string }>();

    /**
     * Formatea la presentación de un producto para su visualización
     *
     * Combina la información de empaque, cantidad y unidad de medida
     * en un string formateado para mostrar al usuario.
     *
     * @param registro - Registro del producto a formatear
     * @returns String con la presentación formateada o 'N/A' si no hay datos
     *
     * @example
     * const presentacion = this.getPresentacion(producto);
     * // Retorna: "Caja 500 ml" o "500 ml" o "N/A"
     */
    getPresentacion(registro: PresentacionProductoComercializador): string {
        const presentacion = registro;
        if (presentacion) {
            const cantidad = presentacion.cantidad || '';
            const unidad = presentacion.unidadMedida?.nombre || '';
            const empaque = presentacion.empaque?.nombre || '';

            if (empaque && cantidad && unidad) {
                return `${empaque} ${cantidad} ${unidad}`;
            } else if (cantidad && unidad) {
                return `${cantidad} ${unidad}`;
            }
        }
        return 'N/A';
    }

    /**
     * Maneja el cambio de estado de un producto
     *
     * Emite el evento `cambiarEstado` con la presentación del producto
     * cuyo estado se desea modificar.
     *
     * @param presentacion - Presentación del producto a modificar
     *
     * @example
     * this.onCambiarEstado(productoSeleccionado);
     */
    onCambiarEstado(presentacion: PresentacionProductoComercializador) {
        this.cambiarEstado.emit(presentacion);
    }

    /**
     * Maneja la navegación a la página anterior
     *
     * Emite el evento `paginaAnterior` para solicitar la carga
     * de los registros de la página anterior.
     *
     * @example
     * this.onPaginaAnterior();
     */
    onPaginaAnterior() {
        this.paginaAnterior.emit();
    }

    /**
     * Maneja la navegación a la página siguiente
     *
     * Emite el evento `paginaSiguiente` para solicitar la carga
     * de los registros de la página siguiente.
     *
     * @example
     * this.onPaginaSiguiente();
     */
    onPaginaSiguiente() {
        this.paginaSiguiente.emit();
    }

    onFiltro(columna: string, valor: string): void {
        this.filtrar.emit({ columna, valor }); //  Emite el evento al padre
    }

    /**
     * Carga las categorías de productos desde el servicio
     * @private
     */
    private async cargarCategorias(): Promise<void> {
        try {
            this.categoriasProductos =
                await this.presentacionService.getTypeProduct();
        } catch (err) {
            console.error('Error al cargar tipos de producto:', err);
        }
    }
}
